import { Injectable } from '@angular/core';
import {
  Observable,
  catchError,
  concatMap,
  defer,
  forkJoin,
  map,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { ApiHttpError } from '../../shared/services/http-client/api-http-error.model';
import { EnumStatusResponse } from '../enum/enumHttpResponse';
import {
  QUESTIONARIO_STATUS_PUBLICADO,
  QUESTIONARIO_STATUS_RASCUNHO,
  StatusQuestionarioApi,
  mapStatusDomainToApi,
} from '../api/models/questionario-status-api';
import { QuestionarioAlternativaResponseDto } from '../api/models/questionario-alternativa-response.dto';
import { PerfilInvestimentoPontuacaoResponseDto } from '../api/models/perfil-investimento-pontuacao-response.dto';
import { QuestionarioPerguntaResponseDto } from '../api/models/questionario-pergunta-response.dto';
import { QuestionarioResponseDto } from '../api/models/questionario-response.dto';
import {
  mapAlternativaFormValueToCommand,
  mapPerguntaFormValueToCommand,
  mapPerguntaResponse,
  mapQuestionarioDto,
  mapQuestionarioFormValueToCommand,
  mapQuestionarioFormValueToCriarRequest,
  mapQuestionarioResponseToFormValue,
} from '../mappers/questionario.mapper';
import {
  QuestionarioAlternativa,
  QuestionarioFormValue,
  QuestionarioPergunta,
  QuestionarioPerfil,
} from '../models/questionario-form.model';
import { QuestionarioSaveError } from '../models/questionario-save-error.model';
import { Questionario, QuestionarioFiltros } from '../models/questionario.model';
import { QuestionarioApiService } from './questionario-api.service';

type Mutation = () => Observable<unknown>;

interface QuestionarioSaveSnapshot {
  header: QuestionarioResponseDto;
  perguntas: QuestionarioPerguntaResponseDto[];
  alternativas: QuestionarioAlternativaResponseDto[];
  pontuacoes: PerfilInvestimentoPontuacaoResponseDto[];
}

interface SaveJournal {
  compensations: Mutation[];
  incompleteReasons: unknown[];
}

class MissingQuestionarioIdentifierError extends Error {
  constructor(resource: string) {
    super(`A API não retornou um identificador válido para ${resource}.`);
    this.name = 'MissingQuestionarioIdentifierError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

@Injectable({
  providedIn: 'root',
})
export class QuestionarioService {
  constructor(private readonly questionarioApiService: QuestionarioApiService) {}

  listarQuestionarios(filtros?: QuestionarioFiltros): Observable<Questionario[]> {
    return this.questionarioApiService
      .listar({ nome: filtros?.nome, status: mapStatusDomainToApi(filtros?.status) })
      .pipe(map((dtos) => dtos.map(mapQuestionarioDto)));
  }

  obterQuestionarioParaEdicao(id: number): Observable<QuestionarioFormValue | null> {
    return this.questionarioApiService.obter(id).pipe(
      switchMap((dto) =>
        forkJoin({
          perguntas: this.questionarioApiService.listarPerguntas(id),
          alternativas: this.questionarioApiService.listarAlternativas(id),
        }).pipe(
          map(({ perguntas, alternativas }) =>
            mapQuestionarioResponseToFormValue(dto, perguntas, alternativas),
          ),
        ),
      ),
      catchError((error: unknown) => {
        if (error instanceof ApiHttpError && error.status === EnumStatusResponse.NotFound) {
          return of(null);
        }

        return throwError(() => error);
      }),
    );
  }

  /**
   * Criação de questionário: uma única chamada a `POST /v1/Questionario`, com perguntas,
   * alternativas e pontuações no mesmo corpo. Não há complemento depois, e por isso também
   * não há estado parcial a compensar se a requisição falhar — diferente da edição, que
   * sincroniza os dependentes um a um.
   */
  criarQuestionario(
    formValue: QuestionarioFormValue,
    status: StatusQuestionarioApi,
  ): Observable<Questionario> {
    const request = mapQuestionarioFormValueToCriarRequest(formValue, status);

    return this.questionarioApiService.criarCompleto(request).pipe(map(mapQuestionarioDto));
  }

  salvarRascunho(formValue: QuestionarioFormValue): Observable<Questionario> {
    return this.gravarCabecalho(formValue, QUESTIONARIO_STATUS_RASCUNHO);
  }

  publicarQuestionario(formValue: QuestionarioFormValue): Observable<Questionario> {
    return this.gravarCabecalho(formValue, QUESTIONARIO_STATUS_PUBLICADO);
  }

  /**
   * Grava o cabeçalho e executa a sincronização com compensação local. O diário
   * é criado por assinatura para que cada tentativa tenha seu próprio estado.
   */
  private gravarCabecalho(
    formValue: QuestionarioFormValue,
    statusApi: string,
  ): Observable<Questionario> {
    return defer(() => {
      const journal: SaveJournal = { compensations: [], incompleteReasons: [] };
      const snapshot$ =
        formValue.id != null
          ? this.carregarSnapshot(formValue.id)
          : of<QuestionarioSaveSnapshot | null>(null);

      return snapshot$.pipe(
        switchMap((snapshot) =>
          this.executarSalvar(formValue, statusApi, snapshot, journal),
        ),
        catchError((error: unknown) => this.tratarFalhaSalvar(error, journal)),
      );
    });
  }

  /** Carrega todos os DTOs que podem ser necessários para restaurar uma edição. */
  private carregarSnapshot(idQuestionario: number): Observable<QuestionarioSaveSnapshot> {
    return forkJoin({
      header: this.questionarioApiService.obter(idQuestionario),
      perguntas: this.questionarioApiService.listarPerguntas(idQuestionario),
      alternativas: this.questionarioApiService.listarAlternativas(idQuestionario),
      pontuacoes: this.questionarioApiService.listarPontuacoes(idQuestionario),
    });
  }

  /** Para um questionário novo, os dependentes são lidos após o POST do cabeçalho. */
  private carregarDependencias(
    idQuestionario: number,
    header: QuestionarioResponseDto,
  ): Observable<QuestionarioSaveSnapshot> {
    return forkJoin({
      perguntas: this.questionarioApiService.listarPerguntas(idQuestionario),
      alternativas: this.questionarioApiService.listarAlternativas(idQuestionario),
      pontuacoes: this.questionarioApiService.listarPontuacoes(idQuestionario),
    }).pipe(map((dependencias) => ({ header, ...dependencias })));
  }

  /**
   * Numa edição o conteúdo vem antes do cabeçalho: perguntas, alternativas e pontuações são
   * criadas e atualizadas primeiro, e só então os dados gerais são gravados. Assim o PUT do
   * cabeçalho — que também carrega o status — encerra o salvamento sobre uma estrutura já
   * completa, em vez de publicar o questionário e depois montar o conteúdo dele.
   *
   * Num questionário novo a ordem é imposta pelo contrato: sem o POST do cabeçalho não há
   * `idQuestionario` para pendurar pergunta nenhuma.
   */
  private executarSalvar(
    formValue: QuestionarioFormValue,
    statusApi: string,
    snapshot: QuestionarioSaveSnapshot | null,
    journal: SaveJournal,
  ): Observable<Questionario> {
    if (formValue.id == null) {
      return this.criarEntaoSincronizar(formValue, statusApi, journal);
    }

    return this.sincronizarEntaoAtualizar(formValue.id, formValue, statusApi, snapshot, journal);
  }

  private sincronizarEntaoAtualizar(
    idQuestionario: number,
    formValue: QuestionarioFormValue,
    statusApi: string,
    snapshot: QuestionarioSaveSnapshot | null,
    journal: SaveJournal,
  ): Observable<Questionario> {
    const snapshot$ = snapshot
      ? of(snapshot)
      : this.carregarSnapshot(idQuestionario);

    return snapshot$.pipe(
      switchMap((estado) =>
        this.executarSincronizacao(idQuestionario, formValue, estado, journal).pipe(
          switchMap(() =>
            this.atualizarCabecalho(idQuestionario, formValue, statusApi, estado, journal),
          ),
        ),
      ),
      map(mapQuestionarioDto),
    );
  }

  private atualizarCabecalho(
    idQuestionario: number,
    formValue: QuestionarioFormValue,
    statusApi: string,
    snapshot: QuestionarioSaveSnapshot,
    journal: SaveJournal,
  ): Observable<QuestionarioResponseDto> {
    const command = mapQuestionarioFormValueToCommand(formValue, statusApi);

    return this.executarMutacao(
      () =>
        this.questionarioApiService.atualizar(idQuestionario, {
          idQuestionario,
          ...command,
        }),
      journal,
      () => {
        const original = snapshot.header;
        if (!original) {
          return this.compensacaoIndisponivel(journal, 'o cabeçalho do questionário');
        }

        return () =>
          this.questionarioApiService.atualizar(idQuestionario, {
            idQuestionario: original.idQuestionario,
            nome: original.nome,
            status: original.status,
            idOperadorInc: original.idOperadorInc,
            idOperadorUltAtu: original.idOperadorUltAtu,
          });
      },
    );
  }

  private criarEntaoSincronizar(
    formValue: QuestionarioFormValue,
    statusApi: string,
    journal: SaveJournal,
  ): Observable<Questionario> {
    const command = mapQuestionarioFormValueToCommand(formValue, statusApi);

    return this.executarMutacao(
      () => this.questionarioApiService.criar(command),
      journal,
      (dto) => {
        const idCriado = this.exigirId(dto.idQuestionario, 'o questionário');
        return () => this.questionarioApiService.excluir(idCriado);
      },
    ).pipe(
      switchMap((dto) => {
        const idQuestionario = this.exigirId(dto.idQuestionario, 'o questionário');

        return this.carregarDependencias(idQuestionario, dto).pipe(
          switchMap((estado) =>
            this.executarSincronizacao(idQuestionario, formValue, estado, journal),
          ),
          map(() => mapQuestionarioDto(dto)),
        );
      }),
    );
  }

  /**
   * Só upserts. Perguntas, alternativas e pontuações que sumiram do formulário não são
   * excluídas aqui: a exclusão acontece no clique da lixeira, por
   * `excluirPergunta`/`excluirAlternativa`/`excluirPontuacaoPerfil`. Salvar e salvar
   * rascunho fazem apenas POST e PUT.
   */
  private executarSincronizacao(
    idQuestionario: number,
    formValue: QuestionarioFormValue,
    snapshot: QuestionarioSaveSnapshot,
    journal: SaveJournal,
  ): Observable<void> {
    return this.sincronizarPerguntas(
      idQuestionario,
      formValue.perguntas,
      snapshot,
      journal,
    ).pipe(
      concatMap(() =>
        this.sincronizarPontuacoes(idQuestionario, formValue.perfis, snapshot, journal),
      ),
    );
  }

  private sincronizarPerguntas(
    idQuestionario: number,
    perguntas: QuestionarioPergunta[],
    snapshot: QuestionarioSaveSnapshot,
    journal: SaveJournal,
  ): Observable<void> {
    const upserts = perguntas.map(
      (pergunta, index) =>
        () =>
          this.gravarPergunta(
            idQuestionario,
            pergunta,
            // A API espera a ordem começando em 1; `index` é 0-based.
            index + 1,
            snapshot,
            journal,
          ),
    );

    return this.runSequentialMutations(upserts);
  }

  private sincronizarPontuacoes(
    idQuestionario: number,
    perfis: QuestionarioPerfil[],
    snapshot: QuestionarioSaveSnapshot,
    journal: SaveJournal,
  ): Observable<void> {
    const preenchidos = perfis.filter(
      (perfil) => perfil.pontuacaoMinima != null || perfil.pontuacaoMaxima != null,
    );

    const upserts = preenchidos.map((perfil) => {
      const existente = snapshot.pontuacoes.find(
        (dto) => dto.idPerfilInvestimento === perfil.idPerfilInvestimento,
      );
      const command = {
        pontuacaoMinima: perfil.pontuacaoMinima,
        pontuacaoMaxima: perfil.pontuacaoMaxima,
      };

      return () =>
        this.executarMutacao(
          () =>
            existente
              ? this.questionarioApiService.atualizarPontuacao(
                  perfil.idPerfilInvestimento,
                  idQuestionario,
                  command,
                )
              : this.questionarioApiService.criarPontuacao(perfil.idPerfilInvestimento, {
                  idQuestionario,
                  ...command,
                }),
          journal,
          (dto) => {
            if (existente) {
              return () =>
                this.questionarioApiService.atualizarPontuacao(
                  perfil.idPerfilInvestimento,
                  idQuestionario,
                  {
                    pontuacaoMinima: existente.pontuacaoMinima,
                    pontuacaoMaxima: existente.pontuacaoMaxima,
                  },
                );
            }

            const idPerfilInvestimento = this.exigirId(
              dto.idPerfilInvestimento,
              'o perfil da pontuação',
            );
            const idQuestionarioCriado = this.exigirId(
              dto.idQuestionario,
              'o questionário da pontuação',
            );
            return () =>
              this.questionarioApiService.excluirPontuacao(
                idPerfilInvestimento,
                idQuestionarioCriado,
              );
          },
        );
    });

    return this.runSequentialMutations(upserts);
  }

  private gravarPergunta(
    idQuestionario: number,
    pergunta: QuestionarioPergunta,
    ordem: number,
    snapshot: QuestionarioSaveSnapshot,
    journal: SaveJournal,
  ): Observable<void> {
    const command = mapPerguntaFormValueToCommand(idQuestionario, pergunta, ordem);
    const pergunta$ = this.executarMutacao(
      () =>
        pergunta.id != null
          ? this.questionarioApiService.atualizarPergunta(pergunta.id, {
              idPergunta: pergunta.id,
              ...command,
            })
          : this.questionarioApiService.criarPergunta(command),
      journal,
      (dto) => {
        if (pergunta.id != null) {
          const original = snapshot.perguntas.find((item) => item.idPergunta === pergunta.id);
          if (!original) {
            return this.compensacaoIndisponivel(journal, 'a pergunta atualizada');
          }

          return () =>
            this.questionarioApiService.atualizarPergunta(pergunta.id!, {
              idPergunta: original.idPergunta,
              texto: original.texto,
              tipo: original.tipo,
              idQuestionario: original.idQuestionario,
              ordem: original.ordem,
            });
        }

        const idPergunta = this.exigirId(dto.idPergunta, 'a pergunta criada');
        return () => this.questionarioApiService.excluirPergunta(idPergunta);
      },
    );

    return pergunta$.pipe(
      switchMap((dto) => {
        const idPergunta = this.exigirId(
          pergunta.id ?? dto.idPergunta,
          'a pergunta',
        );
        return this.sincronizarAlternativas(
          idPergunta,
          pergunta.alternativas,
          snapshot,
          journal,
        );
      }),
    );
  }

  private sincronizarAlternativas(
    idPergunta: number,
    alternativas: QuestionarioAlternativa[],
    snapshot: QuestionarioSaveSnapshot,
    journal: SaveJournal,
  ): Observable<void> {
    const upserts = alternativas.map((alt) => {
      const command = mapAlternativaFormValueToCommand(idPergunta, alt);

      return () =>
        this.executarMutacao(
          () =>
            alt.id != null
              ? this.questionarioApiService.atualizarAlternativa(alt.id, {
                  idAlternativa: alt.id,
                  ...command,
                })
              : this.questionarioApiService.criarAlternativa(command),
          journal,
          (dto) => {
            if (alt.id != null) {
              const original = snapshot.alternativas.find(
                (item) => item.idAlternativa === alt.id,
              );
              if (!original) {
                return this.compensacaoIndisponivel(
                  journal,
                  'a alternativa atualizada',
                );
              }

              return () =>
                this.questionarioApiService.atualizarAlternativa(alt.id!, {
                  idAlternativa: original.idAlternativa,
                  idPergunta: original.idPergunta,
                  texto: original.texto,
                  pontuacao: original.pontuacao,
                });
            }

            const idAlternativa = this.exigirId(
              dto.idAlternativa,
              'a alternativa criada',
            );
            return () => this.questionarioApiService.excluirAlternativa(idAlternativa);
          },
        );
    });

    return this.runSequentialMutations(upserts);
  }

  private executarMutacao<T>(
    request: () => Observable<T>,
    journal: SaveJournal,
    compensationFactory: (result: T) => Mutation,
  ): Observable<T> {
    return defer(request).pipe(
      map((result) => {
        try {
          journal.compensations.push(compensationFactory(result));
          return result;
        } catch (error: unknown) {
          journal.incompleteReasons.push(error);
          throw error;
        }
      }),
    );
  }

  private compensacaoIndisponivel(journal: SaveJournal, resource: string): Mutation {
    const error = new Error(`Não foi possível preparar a restauração de ${resource}.`);
    journal.incompleteReasons.push(error);
    throw error;
  }

  private exigirId(id: number | null | undefined, resource: string): number {
    if (id == null || !Number.isFinite(id) || id <= 0) {
      throw new MissingQuestionarioIdentifierError(resource);
    }

    return id;
  }

  private tratarFalhaSalvar(error: unknown, journal: SaveJournal): Observable<never> {
    if (journal.compensations.length === 0 && journal.incompleteReasons.length === 0) {
      return throwError(() => error);
    }

    const rollbackErrors = [...journal.incompleteReasons];
    return journal.compensations
      .slice()
      .reverse()
      .reduce(
        (chain, compensation) =>
          chain.pipe(
            concatMap(() =>
              defer(compensation).pipe(
                catchError((rollbackError: unknown) => {
                  rollbackErrors.push(rollbackError);
                  return of(undefined);
                }),
              ),
            ),
          ),
        of(null) as Observable<unknown>,
      )
      .pipe(
        switchMap(() =>
          throwError(
            () =>
              new QuestionarioSaveError(
                error,
                rollbackErrors,
                rollbackErrors.length === 0,
              ),
          ),
        ),
      );
  }

  private runSequentialMutations(mutations: Mutation[]): Observable<void> {
    if (mutations.length === 0) {
      return of(undefined);
    }

    return mutations
      .reduce(
        (chain, mutation) => chain.pipe(concatMap(() => defer(mutation))),
        of(null) as Observable<unknown>,
      )
      .pipe(map(() => undefined));
  }

  /** Pontuações já associadas a um questionário, para pré-preencher a seção "Associe o perfil". */
  listarPontuacoes(
    idQuestionario: number,
  ): Observable<Pick<QuestionarioPerfil, 'idPerfilInvestimento' | 'pontuacaoMinima' | 'pontuacaoMaxima'>[]> {
    return this.questionarioApiService.listarPontuacoes(idQuestionario).pipe(
      map((dtos) =>
        dtos.map((dto) => ({
          idPerfilInvestimento: dto.idPerfilInvestimento ?? 0,
          pontuacaoMinima: dto.pontuacaoMinima ?? null,
          pontuacaoMaxima: dto.pontuacaoMaxima ?? null,
        })),
      ),
    );
  }

  /** Perguntas de todos os questionários, para o modal "Selecionar perguntas já cadastradas". */
  listarPerguntasCadastradas(filtros?: { texto?: string }): Observable<QuestionarioPergunta[]> {
    return this.questionarioApiService
      .listarTodasPerguntas(filtros)
      .pipe(map((dtos) => dtos.map((dto) => mapPerguntaResponse(dto, []))));
  }

  /**
   * Alternativas de uma pergunta existente, sem `id`: usadas para clonar a
   * pergunta reaproveitada como um vínculo novo, nunca uma referência compartilhada.
   */
  obterAlternativasParaReaproveitar(idPergunta: number): Observable<QuestionarioAlternativa[]> {
    return this.questionarioApiService.listarAlternativasPorPergunta(idPergunta).pipe(
      map((dtos) => dtos.map((dto) => ({ texto: dto.texto ?? '', pontos: dto.pontuacao ?? 0 }))),
    );
  }

  /**
   * Exclusão imediata de uma pergunta, disparada pela lixeira da tela de questionário. As
   * alternativas já persistidas saem antes da pergunta porque a API não as remove em cascata,
   * e uma alternativa órfã impede a exclusão da pergunta.
   */
  excluirPergunta(idPergunta: number, idsAlternativas: number[]): Observable<void> {
    return this.runSequentialMutations([
      ...idsAlternativas.map(
        (idAlternativa) => () => this.questionarioApiService.excluirAlternativa(idAlternativa),
      ),
      () => this.questionarioApiService.excluirPergunta(idPergunta),
    ]);
  }

  /** Exclusão imediata de uma alternativa já persistida, pelo "−" do modal de pergunta. */
  excluirAlternativa(idAlternativa: number): Observable<void> {
    return this.questionarioApiService.excluirAlternativa(idAlternativa);
  }

  /** Exclusão imediata da pontuação de um perfil, pela lixeira da seção "Associe o perfil". */
  excluirPontuacaoPerfil(
    idPerfilInvestimento: number,
    idQuestionario: number,
  ): Observable<void> {
    return this.questionarioApiService.excluirPontuacao(idPerfilInvestimento, idQuestionario);
  }

  excluirQuestionario(id: number): Observable<boolean> {
    return this.questionarioApiService.excluir(id).pipe(
      map(() => true),
      catchError((error: unknown) => {
        if (error instanceof ApiHttpError && error.status === EnumStatusResponse.NotFound) {
          return of(false);
        }

        return throwError(() => error);
      }),
    );
  }
}
